import React, { useState, useContext } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  SimpleGrid,
  ListItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useDisclosure,
  UnorderedList,
  Box,
  VStack,
  HStack,
  Icon,
  Badge,
  Avatar,
  Container,
  Divider,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Textarea,
  useToast,
  Flex,
  Spacer,
  IconButton,
  Tooltip,
  useBreakpointValue,
} from "@chakra-ui/react";
import MapBoxField from "../../../../components/map/MapBoxField";
import AuthContext from "../../../../contexts/Auth";
import { keyframes } from "@emotion/react";
import {
  FiArrowLeft,
  FiEdit3,
  FiPlus,
  FiPhone,
  FiMapPin,
  FiUsers,
  FiUser,
  FiMail,
  FiHome,
  FiSettings,
  FiTrendingUp,
  FiSearch,
} from "react-icons/fi";
import {useLoaderData, useNavigate, useRevalidator, useParams} from "react-router-dom";
import client from "../../../../helpers/Client";
import InfoBox from '../../../../components/InfoBox';
import PhoneNumber from "../../../../components/buttons/PhoneNumber";
import getFullName from "../../../../helpers/FullName";
import Link from "../../../../components/Link";
import urlToId from "../../../../helpers/UrlToId";
import dayjs from "dayjs";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, CartesianGrid } from "recharts";

export async function resellerDetailLoader({params}) {
  const reseller = await client.get(`/resellers/${params.resellerID}/`);
  const teams = await client.get(`/teams/?reseller=${params.resellerID}`);
  const managers = await client.get(`/employees/?is_manager=true&reseller=${params.resellerID}`);
  const customers = await client.get(`/customers/?last_reseller=${params.resellerID}`);
  const projects = await client.get(`/projects/?reseller=${params.resellerID}`);
  const tickets = await client.get(`/tickets/?assigned_to=${params.resellerID}`);
  const tasks = await client.get(`/tasks/?reseller=${params.resellerID}`);
  return [reseller.data, teams.data, managers.data, customers.data, projects.data, tickets.data, tasks.data]
}

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

export default function ResellerDetail() {
  const auth = useContext(AuthContext);
  const isTechnical = !!auth?.data?.user?.is_technical;
  const { resellerID } = useParams();
  const { isOpen: isOpenTeam, onOpen: onOpenTeam, onClose: onCloseTeam } = useDisclosure();
  const { isOpen: isOpenEdit, onOpen: onOpenEdit, onClose: onCloseEdit } = useDisclosure();
  const [teamName, setTeamName] = useState('');
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const revalidator = useRevalidator();
  const [reseller, team, managers, customers, projects, tickets, tasks] = useLoaderData();
  const navigate = useNavigate();
  const toast = useToast();
  const customersList = Array.isArray(customers) ? customers : (customers?.results || []);
  const projectsList = Array.isArray(projects) ? projects : (projects?.results || []);
  const ticketsList = Array.isArray(tickets) ? tickets : (tickets?.results || []);
  const tasksList = Array.isArray(tasks) ? tasks : (tasks?.results || []);
  const closedStates = new Set(["completed", "cancelled", "deleted"]);
  const ticketOpenCount = ticketsList.filter(t => !closedStates.has(t.state)).length;
  const ticketClosedCount = ticketsList.filter(t => closedStates.has(t.state)).length;
  const tasksInProgress = tasksList.filter(t => t.state === "in_progress").length;
  const months = Array.from({ length: 6 }, (_, i) => dayjs().subtract(5 - i, "month"));
  const ticketMonthly = months.map(m => {
    const label = m.format("MMM");
    const monthTickets = ticketsList.filter(t => dayjs(t.created_at).format("YYYY-MM") === m.format("YYYY-MM"));
    const open = monthTickets.filter(t => !closedStates.has(t.state)).length;
    const closed = monthTickets.filter(t => closedStates.has(t.state)).length;
    return { label, open, closed };
  });
  const taskMonthly = months.map(m => {
    const label = m.format("MMM");
    const monthTasks = tasksList.filter(t => dayjs(t.created_at).format("YYYY-MM") === m.format("YYYY-MM"));
    const progress = monthTasks.filter(t => t.state === "in_progress").length;
    const other = monthTasks.length - progress;
    return { label, progress, other };
  });
  const [customerQuery, setCustomerQuery] = useState("");
  const [projectQuery, setProjectQuery] = useState("");
  const [ticketQuery, setTicketQuery] = useState("");
  const [groupCustomers, setGroupCustomers] = useState(false);
  const [groupProjects, setGroupProjects] = useState(false);
  const filteredCustomers = customersList.filter(c => {
    const name = getFullName(c.user) || "";
    const phone = c.phone_number || "";
    const city = c.city_name || "";
    const q = customerQuery.toLowerCase();
    return name.toLowerCase().includes(q) || phone.toLowerCase().includes(q) || city.toLowerCase().includes(q);
  });
  const filteredProjects = projectsList.filter(p => {
    const q = projectQuery.toLowerCase();
    return (p.name || "").toLowerCase().includes(q) || (p.city_info?.name || "").toLowerCase().includes(q) || (p.district_info?.name || "").toLowerCase().includes(q);
  });
  const groupedCustomers = Object.values(filteredCustomers.reduce((acc, c) => {
    const k = c.city_name || "Şehir Yok";
    if (!acc[k]) acc[k] = { key: k, items: [] };
    acc[k].items.push(c);
    return acc;
  }, {}));
  const groupedProjects = Object.values(filteredProjects.reduce((acc, p) => {
    const k = p.city_info?.name || "Şehir Yok";
    if (!acc[k]) acc[k] = { key: k, items: [] };
    acc[k].items.push(p);
    return acc;
  }, {}));
  const stateColor = (s) => {
    switch (s) {
      case "in_queue": return "yellow";
      case "assignment": return "cyan";
      case "in_progress": return "orange";
      case "approval": return "purple";
      case "completed": return "green";
      case "cancelled": return "gray";
      case "deleted": return "red";
      default: return "blue";
    }
  };
  const sortedTickets = ticketsList.slice().sort((a, b) => (dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf()));
  const filteredTickets = sortedTickets.filter(t => {
    const q = ticketQuery.toLowerCase();
    return (t.phone_number || "").toLowerCase().includes(q) || (t.number || "").toString().includes(q) || (t.state || "").toLowerCase().includes(q);
  });
  
  // Color mode values
  const bgColor = 'white';
  const cardBg = 'rgba(255, 255, 255, 0.9)';
  const borderColor = 'gray.200';
  const textColor = 'gray.700';
  const accentColor = 'blue.600';
  
  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      toast({
        title: 'Hata',
        description: 'Takım adı gereklidir.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setLoading(true);
    try {
      const requestBody = {
        name: teamName,
        reseller: reseller.url
      };
      await client.post(`/teams/`, requestBody);
      toast({
        title: 'Başarılı',
        description: 'Takım başarıyla oluşturuldu.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setTeamName('');
      onCloseTeam();
      revalidator.revalidate();
    } catch (error) {
      toast({
        title: 'Hata',
        description: 'Takım oluşturulurken bir hata oluştu.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditReseller = async () => {
    setLoading(true);
    try {
      const requestBody = {
        name: editName || reseller.name,
        phone_number: editPhone || reseller.phone_number,
        address: editAddress || reseller.address,
      };
      await client.patch(`/resellers/${resellerID}/`, requestBody);
      toast({
        title: 'Başarılı',
        description: 'Bayi bilgileri güncellendi.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onCloseEdit();
      revalidator.revalidate();
    } catch (error) {
      toast({
        title: 'Hata',
        description: 'Güncelleme sırasında bir hata oluştu.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoBack = () => {
    navigate(-1);
  };
  
  const openEditModal = () => {
    setEditName(reseller.name);
    setEditPhone(reseller.phone_number);
    setEditAddress(reseller.address);
    onOpenEdit();
  };

  const sendCredentials = async () => {
    try {
      if (!managers || managers.length === 0) {
        toast({ title: "Bayi yöneticisi yok", status: "warning" });
        return;
      }
      await client.post(`/resellers/${resellerID}/send_credentials/`);
      toast({ title: "Kullanıcı bilgileri gönderildi", status: "success" });
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || String(error);
      toast({ title: "Gönderim başarısız", description: detail, status: "error" });
    }
  };

  return (
    <Container maxW={{ base: '100%', lg: '7xl' }} px={{ base: 4, md: 6 }} py={{ base: 4, md: 8 }}>
      {/* Header */}
      <Flex mb={8} align="center" gap={4} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
        <Tooltip label="Geri Dön">
          <IconButton
            icon={<Icon as={FiArrowLeft} />}
            onClick={handleGoBack}
            variant="ghost"
            size="lg"
            borderRadius="xl"
          />
        </Tooltip>
        <VStack align="start" spacing={1} flex={1}>
          <Heading
            size={{ base: 'lg', md: 'xl' }}
            color={accentColor}
            animation={`${fadeIn} 0.6s ease-out`}
          >
            {reseller.name}
          </Heading>
          <Text color={textColor} fontSize="md">
            Bayi Detayları
          </Text>
        </VStack>
        <HStack spacing={3} flexWrap={{ base: 'wrap', md: 'nowrap' }}>

          <Button
            leftIcon={<Icon as={FiPlus} />}
            colorScheme="green"
            onClick={onOpenTeam}
            borderRadius="xl"
          >
            Takım Ekle
          </Button>
          {isTechnical && (
            <Button
              leftIcon={<Icon as={FiMail} />}
              colorScheme="blue"
              onClick={sendCredentials}
              borderRadius="xl"
              variant="outline"
            >
              Kullanıcı Bilgilerini Gönder
            </Button>
          )}
        </HStack>
      </Flex>

      <VStack spacing={8} align="stretch">
        {/* Main Info Card */}
        <Card
          bg={cardBg}
          backdropFilter="blur(20px)"
          border="1px solid"
          borderColor={borderColor}
          borderRadius="2xl"
          overflow="hidden"
          animation={`${slideUp} 0.8s ease-out`}
        >
          <CardHeader>
            <HStack spacing={3}>
              <Icon as={FiHome} color="blue.500" boxSize={6} />
              <Heading size="md">Bayi Bilgileri</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
              <VStack align="start" spacing={3}>
                <HStack spacing={2}>
                  <Icon as={FiPhone} color="green.500" />
                  <Text fontWeight="semibold" color={textColor}>Telefon</Text>
                </HStack>
                <PhoneNumber number={reseller.phone_number} />
              </VStack>
              
              <VStack align="start" spacing={3}>
                <HStack spacing={2}>
                  <Icon as={FiMapPin} color="red.500" />
                  <Text fontWeight="semibold" color={textColor}>Adres</Text>
                </HStack>
                <Text color={textColor}>{reseller.address}</Text>
              </VStack>
              
              <VStack align="start" spacing={3}>
                <HStack spacing={2}>
                  <Icon as={FiUser} color="purple.500" />
                  <Text fontWeight="semibold" color={textColor}>Yetkili Kişiler</Text>
                </HStack>
                <VStack align="start" spacing={1}>
                  {managers.length > 0 ? (
                    managers.map(m => (
                      <HStack key={m.user.url} spacing={2}>
                        <Avatar size="xs" name={getFullName(m.user)} />
                        <Text fontSize="sm" color={textColor}>{getFullName(m.user)}</Text>
                      </HStack>
                    ))
                  ) : (
                    <Text fontSize="sm" color="gray.500">Yetkili kişi bulunmuyor</Text>
                  )}
                </VStack>
              </VStack>
              
              <VStack align="start" spacing={3}>
                <HStack spacing={2}>
                  <Icon as={FiUsers} color="teal.500" />
                  <Text fontWeight="semibold" color={textColor}>Takımlar</Text>
                </HStack>
                <VStack align="start" spacing={1}>
                  {team.length > 0 ? (
                    team.map(t => (
                      <HStack key={t.url} spacing={2}>
                        <Badge colorScheme="blue" borderRadius="full">
                          {t.member_count}
                        </Badge>
                        <Text fontSize="sm" color={textColor}>{t.name}</Text>
                      </HStack>
                    ))
                  ) : (
                    <Text fontSize="sm" color="gray.500">Takım bulunmuyor</Text>
                  )}
                </VStack>
              </VStack>
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Stats Card */}
        <Card
          bg={cardBg}
          backdropFilter="blur(20px)"
          border="1px solid"
          borderColor={borderColor}
          borderRadius="2xl"
          overflow="hidden"
          animation={`${slideUp} 0.8s ease-out 0.2s both`}
        >
          <CardHeader>
            <HStack spacing={3}>
              <Icon as={FiSettings} color="orange.500" boxSize={6} />
              <Heading size="md">İstatistikler</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <Stat>
                <StatLabel>Toplam Takım</StatLabel>
                <StatNumber color={accentColor}>{team.length}</StatNumber>
                <StatHelpText>Aktif takım sayısı</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Toplam Üye</StatLabel>
                <StatNumber color={accentColor}>
                  {team.reduce((total, t) => total + t.member_count, 0)}
                </StatNumber>
                <StatHelpText>Tüm takımlardaki üye sayısı</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Yönetici</StatLabel>
                <StatNumber color={accentColor}>{managers.length}</StatNumber>
                <StatHelpText>Yetkili kişi sayısı</StatHelpText>
              </Stat>
            </SimpleGrid>
          </CardBody>
        </Card>

        <Card
          bg={cardBg}
          backdropFilter="blur(20px)"
          border="1px solid"
          borderColor={borderColor}
          borderRadius="2xl"
          overflow="hidden"
          animation={`${slideUp} 0.8s ease-out 0.25s both`}
        >
          <CardHeader>
            <HStack spacing={3}>
              <Icon as={FiTrendingUp} color="pink.500" boxSize={6} />
              <Heading size="md">Performans</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 3, lg: 6 }} spacing={6}>
              <Stat>
                <StatLabel>Müşteri</StatLabel>
                <StatNumber color={accentColor}>{customersList.length}</StatNumber>
                <StatHelpText>Toplam müşteri</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Proje</StatLabel>
                <StatNumber color={accentColor}>{projectsList.length}</StatNumber>
                <StatHelpText>Toplam proje</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Açık İş Emri</StatLabel>
                <StatNumber color={accentColor}>{ticketOpenCount}</StatNumber>
                <StatHelpText>Aktif iş emirleri</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Kapalı İş Emri</StatLabel>
                <StatNumber color={accentColor}>{ticketClosedCount}</StatNumber>
                <StatHelpText>Tamamlanan</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Görev</StatLabel>
                <StatNumber color={accentColor}>{tasksList.length}</StatNumber>
                <StatHelpText>Toplam görev</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Devam Eden</StatLabel>
                <StatNumber color={accentColor}>{tasksInProgress}</StatNumber>
                <StatHelpText>İlerleyen görevler</StatHelpText>
              </Stat>
            </SimpleGrid>
          </CardBody>
        </Card>

        <Card
          bg={cardBg}
          backdropFilter="blur(20px)"
          border="1px solid"
          borderColor={borderColor}
          borderRadius="2xl"
          overflow="hidden"
          animation={`${slideUp} 0.8s ease-out 0.3s both`}
        >
          <CardHeader>
            <HStack spacing={3}>
              <Icon as={FiUsers} color="teal.500" boxSize={6} />
              <Heading size="md">Müşteriler</Heading>
              <Spacer />
              <Button size="sm" variant="outline" onClick={() => setGroupCustomers(v => !v)}>
                {groupCustomers ? "Düz Liste" : "Şehre Göre Grupla"}
              </Button>
              <Link to={`/musteriler/?last_reseller=${resellerID}`}>
                <Button size="sm" variant="outline">Tümünü Gör</Button>
              </Link>
            </HStack>
          </CardHeader>
          <CardBody>
            <InputGroup mb={4}>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input placeholder="Ara (ad, telefon, şehir)" value={customerQuery} onChange={e => setCustomerQuery(e.target.value)} />
            </InputGroup>
            {groupCustomers ? (
              <Accordion allowMultiple>
                {groupedCustomers.map(group => (
                  <AccordionItem key={group.key}>
                    <h2>
                      <AccordionButton>
                        <HStack flex="1" textAlign="left" spacing={3}>
                          <Badge colorScheme="blue">{group.items.length}</Badge>
                          <Text fontWeight="semibold" color={textColor}>{group.key}</Text>
                        </HStack>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                        {group.items.slice(0, 9).map((c) => (
                          <HStack key={c.url} spacing={3}>
                            <Avatar size="sm" name={getFullName(c.user)} />
                            <VStack align="start" spacing={0}>
                              <Link to={`/musteriler/${urlToId(c.url)}`}>
                                <Text fontWeight="semibold" color={textColor}>{getFullName(c.user)}</Text>
                              </Link>
                              <HStack spacing={2}>
                                <Text fontSize="sm" color="gray.500">{c.city_name || ""}</Text>
                                {c.phone_number ? (<Badge colorScheme="blue">{c.phone_number}</Badge>) : null}
                              </HStack>
                            </VStack>
                          </HStack>
                        ))}
                      </SimpleGrid>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
                {groupedCustomers.length === 0 && (
                  <Text fontSize="sm" color="gray.500">Müşteri bulunmuyor</Text>
                )}
              </Accordion>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {filteredCustomers.slice(0, 9).map((c) => (
                  <HStack key={c.url} spacing={3}>
                    <Avatar size="sm" name={getFullName(c.user)} />
                    <VStack align="start" spacing={0}>
                      <Link to={`/musteriler/${urlToId(c.url)}`}>
                        <Text fontWeight="semibold" color={textColor}>{getFullName(c.user)}</Text>
                      </Link>
                      <HStack spacing={2}>
                        <Text fontSize="sm" color="gray.500">{c.city_name || ""}</Text>
                        {c.phone_number ? (<Badge colorScheme="blue">{c.phone_number}</Badge>) : null}
                      </HStack>
                    </VStack>
                  </HStack>
                ))}
                {(filteredCustomers.length === 0) && (
                  <Text fontSize="sm" color="gray.500">Müşteri bulunmuyor</Text>
                )}
              </SimpleGrid>
            )}
          </CardBody>
        </Card>

        <Card
          bg={cardBg}
          backdropFilter="blur(20px)"
          border="1px solid"
          borderColor={borderColor}
          borderRadius="2xl"
          overflow="hidden"
          animation={`${slideUp} 0.8s ease-out 0.35s both`}
        >
          <CardHeader>
            <HStack spacing={3}>
              <Icon as={FiHome} color="blue.500" boxSize={6} />
              <Heading size="md">Projeler</Heading>
              <Spacer />
              <Button size="sm" variant="outline" onClick={() => setGroupProjects(v => !v)}>
                {groupProjects ? "Düz Liste" : "Şehre Göre Grupla"}
              </Button>
              <Link to={`/proje/?reseller=${resellerID}`}>
                <Button size="sm" variant="outline">Tümünü Gör</Button>
              </Link>
            </HStack>
          </CardHeader>
          <CardBody>
            <InputGroup mb={4}>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input placeholder="Ara (ad, şehir, ilçe)" value={projectQuery} onChange={e => setProjectQuery(e.target.value)} />
            </InputGroup>
            {groupProjects ? (
              <Accordion allowMultiple>
                {groupedProjects.map(group => (
                  <AccordionItem key={group.key}>
                    <h2>
                      <AccordionButton>
                        <HStack flex="1" textAlign="left" spacing={3}>
                          <Badge colorScheme="blue">{group.items.length}</Badge>
                          <Text fontWeight="semibold" color={textColor}>{group.key}</Text>
                        </HStack>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                        {group.items.slice(0, 9).map((p) => (
                          <HStack key={p.url} spacing={3}>
                            <Badge colorScheme="blue" borderRadius="full">{p.nof_units}</Badge>
                            <VStack align="start" spacing={0}>
                              <Link to={`/proje/${urlToId(p.url)}`}>
                                <Text fontWeight="semibold" color={textColor}>{p.name}</Text>
                              </Link>
                              <HStack spacing={2}>
                                <Text fontSize="sm" color="gray.500">{p.city_info?.name || ""}</Text>
                                <Badge colorScheme="green">Açık {p.num_of_open_tickets || 0}</Badge>
                                <Badge colorScheme="gray">Kapalı {p.num_of_closed_tickets || 0}</Badge>
                              </HStack>
                            </VStack>
                          </HStack>
                        ))}
                      </SimpleGrid>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
                {groupedProjects.length === 0 && (
                  <Text fontSize="sm" color="gray.500">Proje bulunmuyor</Text>
                )}
              </Accordion>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {filteredProjects.slice(0, 9).map((p) => (
                  <HStack key={p.url} spacing={3}>
                    <Badge colorScheme="blue" borderRadius="full">{p.nof_units}</Badge>
                    <VStack align="start" spacing={0}>
                      <Link to={`/proje/${urlToId(p.url)}`}>
                        <Text fontWeight="semibold" color={textColor}>{p.name}</Text>
                      </Link>
                      <HStack spacing={2}>
                        <Text fontSize="sm" color="gray.500">{p.city_info?.name || ""}</Text>
                        <Badge colorScheme="green">Açık {p.num_of_open_tickets || 0}</Badge>
                        <Badge colorScheme="gray">Kapalı {p.num_of_closed_tickets || 0}</Badge>
                      </HStack>
                    </VStack>
                  </HStack>
                ))}
                {(filteredProjects.length === 0) && (
                  <Text fontSize="sm" color="gray.500">Proje bulunmuyor</Text>
                )}
              </SimpleGrid>
            )}
          </CardBody>
        </Card>

        <Card
          bg={cardBg}
          backdropFilter="blur(20px)"
          border="1px solid"
          borderColor={borderColor}
          borderRadius="2xl"
          overflow="hidden"
          animation={`${slideUp} 0.8s ease-out 0.45s both`}
        >
          <CardHeader>
            <HStack spacing={3}>
              <Icon as={FiSettings} color="orange.500" boxSize={6} />
              <Heading size="md">İş Emirleri</Heading>
              <Spacer />
              <Link to={`/is-emri/?assigned_to=${resellerID}`}>
                <Button size="sm" variant="outline">Tümünü Gör</Button>
              </Link>
            </HStack>
          </CardHeader>
          <CardBody>
            <InputGroup mb={4}>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input placeholder="Ara (telefon, no, durum)" value={ticketQuery} onChange={e => setTicketQuery(e.target.value)} />
            </InputGroup>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {filteredTickets.slice(0, 9).map((t) => (
                <HStack key={t.url} spacing={3}>
                  <Badge colorScheme={stateColor(t.state)} borderRadius="full">#{t.number}</Badge>
                  <VStack align="start" spacing={0}>
                    <Link to={`/is-emri/${urlToId(t.url)}`}>
                      <Text fontWeight="semibold" color={textColor}>{t.phone_number || ""}</Text>
                    </Link>
                    <HStack spacing={2}>
                      <Text fontSize="sm" color="gray.500">{t.state}</Text>
                      <Text fontSize="sm" color="gray.400">{dayjs(t.created_at).format("DD.MM.YYYY")}</Text>
                    </HStack>
                  </VStack>
                </HStack>
              ))}
              {(filteredTickets.length === 0) && (
                <Text fontSize="sm" color="gray.500">İş emri bulunmuyor</Text>
              )}
            </SimpleGrid>
          </CardBody>
        </Card>

        <Card
          bg={cardBg}
          backdropFilter="blur(20px)"
          border="1px solid"
          borderColor={borderColor}
          borderRadius="2xl"
          overflow="hidden"
          animation={`${slideUp} 0.8s ease-out 0.55s both`}
        >
          <CardHeader>
            <HStack spacing={3}>
              <Icon as={FiSettings} color="purple.500" boxSize={6} />
              <Heading size="md">Görevler</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {(Array.isArray(tasks) ? tasks : (tasks?.results || [])).slice(0, 9).map((tsk) => (
                <HStack key={tsk.url} spacing={3}>
                  <Badge colorScheme="purple" borderRadius="full">#{tsk.number}</Badge>
                  <VStack align="start" spacing={0}>
                    <Text fontWeight="semibold" color={textColor}>{tsk.title}</Text>
                    <Text fontSize="sm" color="gray.500">{tsk.state}</Text>
                  </VStack>
                </HStack>
              ))}
              {((Array.isArray(tasks) ? tasks.length : (tasks?.results || []).length) === 0) && (
                <Text fontSize="sm" color="gray.500">Görev bulunmuyor</Text>
              )}
            </SimpleGrid>
          </CardBody>
        </Card>

        <Card
          bg={cardBg}
          backdropFilter="blur(20px)"
          border="1px solid"
          borderColor={borderColor}
          borderRadius="2xl"
          overflow="hidden"
          animation={`${slideUp} 0.8s ease-out 0.65s both`}
        >
          <CardHeader>
            <HStack spacing={3}>
              <Icon as={FiTrendingUp} color="cyan.500" boxSize={6} />
              <Heading size="md">Trendler</Heading>
            </HStack>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Box w="100%" h={{ base: 240, md: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={ticketMonthly} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis allowDecimals={false} />
                    <ReTooltip />
                    <Line type="monotone" dataKey="open" stroke="#3182ce" strokeWidth={2} dot={false} name="Açık" />
                    <Line type="monotone" dataKey="closed" stroke="#dd6b20" strokeWidth={2} dot={false} name="Kapalı" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
              <Box w="100%" h={{ base: 240, md: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={taskMonthly} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis allowDecimals={false} />
                    <ReTooltip />
                    <Bar dataKey="progress" stackId="a" fill="#805ad5" name="İlerleyen" />
                    <Bar dataKey="other" stackId="a" fill="#90cdf4" name="Diğer" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </SimpleGrid>
          </CardBody>
        </Card>
      </VStack>
      {/* Create Team Modal */}
      <Modal
        isOpen={isOpenTeam}
        onClose={onCloseTeam}
        motionPreset="slideInBottom"
        size={{ base: 'md', md: 'lg' }}
        closeOnOverlayClick={false}
      >
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent
          bg={cardBg}
          borderRadius="2xl"
          border="1px solid"
          borderColor={borderColor}
        >
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FiPlus} color="green.500" />
              <Text>Yeni Takım Oluştur</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium">Takım Adı</FormLabel>
              <Input
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="Takım adını giriniz"
                borderRadius="lg"
                _focus={{
                  borderColor: 'blue.400',
                  boxShadow: '0 0 0 1px rgba(66, 153, 225, 0.6)',
                }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                colorScheme="green"
                onClick={handleCreateTeam}
                isLoading={loading}
                loadingText="Oluşturuluyor..."
                isDisabled={!teamName.trim()}
                borderRadius="lg"
              >
                Oluştur
              </Button>
              <Button
                variant="ghost"
                onClick={onCloseTeam}
                borderRadius="lg"
              >
                Vazgeç
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Reseller Modal */}
      <Modal
        isOpen={isOpenEdit}
        onClose={onCloseEdit}
        motionPreset="slideInBottom"
        size={{ base: 'md', md: 'lg' }}
        closeOnOverlayClick={false}
      >
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent
          bg={cardBg}
          borderRadius="2xl"
          border="1px solid"
          borderColor={borderColor}
        >
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FiEdit3} color="blue.500" />
              <Text>Bayi Bilgilerini Düzenle</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Bayi Adı</FormLabel>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  borderRadius="lg"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Telefon</FormLabel>
                <Input
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  borderRadius="lg"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Adres</FormLabel>
                <Textarea
                  value={editAddress}
                  onChange={(e) => setEditAddress(e.target.value)}
                  borderRadius="lg"
                  rows={3}
                />
                <Box mt={2}><MapBoxField value={editAddress} onChangeAddress={setEditAddress} /></Box>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                colorScheme="blue"
                onClick={handleEditReseller}
                isLoading={loading}
                loadingText="Güncelleniyor..."
                borderRadius="lg"
              >
                Güncelle
              </Button>
              <Button
                variant="ghost"
                onClick={onCloseEdit}
                borderRadius="lg"
              >
                Vazgeç
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
}
